// =============================================================================
// MeuDriver — CSV export helper
// -----------------------------------------------------------------------------
// Gera um Blob CSV a partir de um array de objetos e dispara download.
// Sem dependências externas — usa apenas Blob + URL.createObjectURL.
//
// Convenções:
//   * Separador: `;` (vírgula causa ambiguidade com decimais em pt-BR).
//   * Encoding: UTF-8 com BOM (Excel pt-BR só interpreta acentos com BOM).
//   * Quoting: todo valor é envolvido em "", e " interno é dobrado.
//   * Datas: o caller formata antes (este helper não opina sobre formato).
//
// Uso:
//   downloadCsv('extrato-aluno-2026-05.csv', rows, [
//     { key: 'date',       label: 'Data' },
//     { key: 'instructor', label: 'Instrutor' },
//     { key: 'amount',     label: 'Valor (R$)', map: (v) => formatBRL(v) },
//   ])
// =============================================================================

export interface CsvColumn<T> {
  key: keyof T | string
  label: string
  /** Transformação opcional da célula. Recebe `row[key]`, retorna string. */
  map?: (value: unknown, row: T) => string
}

/**
 * Escapa um valor para célula CSV (RFC 4180-like).
 *  - Converte null/undefined em "".
 *  - Dobra aspas internas.
 *  - Prefixo com apóstrofe em valores que iniciem com =, +, -, @, tab ou CR
 *    para prevenir CSV injection (OWASP CWE-1236).
 *  - Envolve em aspas duplas.
 */
function escapeCell(value: unknown): string {
  if (value === null || value === undefined) return '""'
  let str = String(value).replace(/"/g, '""')
  // Previne CSV injection (OWASP CWE-1236): caracteres de fórmula no início.
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str
  }
  return `"${str}"`
}

/**
 * Constrói o conteúdo CSV (sem download).
 */
export function buildCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const header = columns.map((c) => escapeCell(c.label)).join(';')
  const lines = rows.map((row) => {
    return columns
      .map((c) => {
        const raw = (row as Record<string, unknown>)[c.key as string]
        const cell = c.map ? c.map(raw, row) : raw
        return escapeCell(cell)
      })
      .join(';')
  })
  return [header, ...lines].join('\r\n')
}

/**
 * Gera Blob CSV e dispara download no navegador.
 */
export function downloadCsv<T>(
  filename: string,
  rows: T[],
  columns: CsvColumn<T>[],
): void {
  const csv = buildCsv(rows, columns)
  // BOM UTF-8 para Excel reconhecer acentos.
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  } finally {
    // Libera memória — alguns navegadores demoram a coletar.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}

/**
 * Helper de formato brasileiro para reuso nos `map`s.
 */
export function formatBRL(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!isFinite(n)) return String(value)
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function formatDateBR(value: unknown): string {
  if (!value) return ''
  try {
    const d = new Date(String(value))
    if (isNaN(d.getTime())) return String(value)
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch {
    return String(value)
  }
}

export function formatDateTimeBR(value: unknown): string {
  if (!value) return ''
  try {
    const d = new Date(String(value))
    if (isNaN(d.getTime())) return String(value)
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return String(value)
  }
}
